import { useDrag } from '@use-gesture/react';
import {
  PropsWithChildren,
  cloneElement,
  isValidElement,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { animated, config, useSpring } from 'react-spring';
import { useMeasure } from 'react-use';
import styled from 'styled-components';

import { Vertical } from 'atoms/layout/flex';
import { useScreenSize } from 'hooks/device/useScreenSize';
import { thickMaterial } from 'style/utils';

import { Portal } from '../Portal';

// add extra height to the bottom to allow user to swipe pass the bottom without seeing behind the sheet
const EXTRA_BOTTOM_PADDING = 80;
const THRESHOLD_TO_CANCEL_SWIPE = 10;

const Grabber = styled.div`
  background: var(--c-white);
  width: 36px;
  height: var(--half-unit);
  border-radius: var(--half-unit);
  margin-top: var(--intermediate-unit);
`;

const Root = styled(Vertical)`
  position: relative;
  justify-content: flex-end;
  overflow: clip;
  pointer-events: none;
`;

const AnimatedDiv = styled(animated.div)`
  touch-action: none;
`;

const Content = styled(Vertical)`
  ${thickMaterial}
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: var(--intermediate-unit) var(--intermediate-unit) 0 0;
  padding-bottom: ${EXTRA_BOTTOM_PADDING}px;
  pointer-events: auto;
`;
const PortalReceiver = styled.div`
  padding: 0 var(--unit);
  a,
  button,
  label {
    /** only interractive elements should be able to receive pointer events */
    pointer-events: auto;
  }
`;
type Props = {
  snapPoints: number[];
  openInitially?: boolean;
  onChange?: (value: boolean) => void;
};

const BottomSheetWrapper = ({
  snapPoints,
  children,
  openInitially = true,
  onChange,
}: PropsWithChildren<Props>) => {
  const allowedSnapSpoints = [
    EXTRA_BOTTOM_PADDING,
    ...snapPoints.map(sp => sp + EXTRA_BOTTOM_PADDING),
  ];
  const bottomBound = allowedSnapSpoints.at(-1)!;
  const [ref, { height }] = useMeasure<HTMLDivElement>();

  const [{ y }, api] = useSpring(() => ({
    y: allowedSnapSpoints.at(openInitially ? 0 : -1)!,
  }));

  const snapTo = (to: number, index: number) => {
    api.start({
      y: to,
      immediate: false,
      config: config.stiff,
    });
    onChange?.(index !== 0);
  };
  useLayoutEffect(() => {
    const docStyle = document.documentElement.style;
    if (height) {
      docStyle.setProperty(
        '--bottomsheet-shrinked-height',
        `${height - bottomBound}px`
      );
    }
    return () => {
      docStyle.setProperty('--bottomsheet-shrinked-height', '0px');
    };
  });
  const bind = useDrag(
    ({ last, offset: [, oy], cancel, tap }) => {
      if (tap) {
        return;
      }
      // cancel if user tries to swipe down too far
      if (oy < THRESHOLD_TO_CANCEL_SWIPE) {
        cancel();
      }

      if (last) {
        // snap to the nearest snap point
        const reverseSnapPoints = [...allowedSnapSpoints].reverse();
        const snapIndex = reverseSnapPoints.findIndex(
          snapPoint => oy > snapPoint * 0.5
        );
        if (snapIndex !== -1) {
          snapTo(reverseSnapPoints[snapIndex], snapIndex);
        } else {
          snapTo(reverseSnapPoints.at(-1)!, snapIndex);
        }
      } else {
        api.start({ y: oy, immediate: true });
      }
    },
    {
      from: () => [0, y.get()],
      bounds: { bottom: bottomBound },
      filterTaps: true,
      rubberband: true,
    }
  );

  return (
    <Portal id="above-bottom-bar-portal">
      <Root
        gap={0}
        style={{
          marginTop: `calc(-1 * (${bottomBound}px + var(--bottom-bar-height-mobile)))`,
        }}
      >
        <AnimatedDiv {...bind()} style={{ y }} ref={ref}>
          <PortalReceiver id="above-bottom-sheet-portal" />
          <Content gap={2.5} center>
            {allowedSnapSpoints.length > 1 && <Grabber />}
            {children}
          </Content>
        </AnimatedDiv>
      </Root>
    </Portal>
  );
};

/**
 * BottomSheetAbove is a component that renders the children in a portal if the screen is mobile
 * and adds a className to the children if the screen is desktop
 */
const BottomSheetAbove = ({ children }: PropsWithChildren) => {
  const { up: isLaptop } = useScreenSize('laptop');
  const portalReceiver = document.getElementById('above-bottom-sheet-portal');
  if (portalReceiver && !isLaptop) {
    return createPortal(children, portalReceiver);
  }
  return isValidElement<{ className?: string }>(children)
    ? cloneElement(children, { className: 'portalDisabled' })
    : null;
};

/** BottomSheet is a mobile only component, it will render nothing on desktop */
export const BottomSheet = (props: PropsWithChildren<Props>) => {
  const { up: isLaptop } = useScreenSize('laptop');
  if (isLaptop) {
    return null;
  }
  return <BottomSheetWrapper {...props} />;
};

BottomSheet.Above = BottomSheetAbove;
